import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageProvider";

interface WorkoutCountdownProps {
  onComplete: () => void;
  controlledRemainingMs?: number;
}

const STEPS = [3, 2, 1, 0]; // 0 = "GO!"

const WorkoutCountdown: React.FC<WorkoutCountdownProps> = ({ onComplete, controlledRemainingMs }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (controlledRemainingMs !== undefined) return;
    if (step >= STEPS.length) {
      onComplete();
      return;
    }
    const timer = setTimeout(() => setStep(s => s + 1), 800);
    return () => clearTimeout(timer);
  }, [step, onComplete, controlledRemainingMs]);

  const displayStep = controlledRemainingMs === undefined ? step : Math.min(3, Math.max(0, 3 - Math.floor(Math.max(0, controlledRemainingMs - 1) / 800)));
  const current = STEPS[displayStep];
  if (controlledRemainingMs === undefined && step >= STEPS.length) return null;

  const isGo = current === 0;
  const display = isGo ? t("fitness.workout.countdown.go") : String(current);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(5,8,16,0.95)" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={displayStep}
          className="font-display font-bold select-none"
          style={{
            fontSize: isGo ? "6rem" : "8rem",
            lineHeight: 1,
            color: isGo ? "hsl(187 72% 50%)" : "#ffffff",
            textShadow: isGo
              ? "0 0 40px hsl(187 72% 50% / 0.6), 0 0 80px hsl(187 72% 50% / 0.3)"
              : "0 0 20px rgba(255,255,255,0.15)",
          }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.4 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {display}
        </motion.span>
      </AnimatePresence>
    </motion.div>
  );
};

export default React.memo(WorkoutCountdown);
